import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

declare global {
   interface Window {
      AppleID?: any;
   }
}

interface UseAppleAuthOptions {
   onSuccess: (idToken: string) => Promise<void> | void;
   onError?: (error: string) => void;
}

const APPLE_JS_SRC =
   'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

export function useAppleAuth({ onSuccess, onError }: UseAppleAuthOptions) {
   const [isReady, setIsReady] = useState(false);

   useEffect(() => {
      const clientId = process.env.REACT_APP_APPLE_SERVICE_ID;
      const redirectURI =
         process.env.REACT_APP_APPLE_REDIRECT_URI || `${window.location.origin}/auth/apple/callback`;

      if (!clientId) {
         console.warn('REACT_APP_APPLE_SERVICE_ID is not set');
         return;
      }

      const existingScript = document.querySelector<HTMLScriptElement>(
         `script[src="${APPLE_JS_SRC}"]`
      );

      const initApple = () => {
         try {
            if (!window.AppleID) {
               console.error('AppleID global not available after script load');
               return;
            }

            window.AppleID.auth.init({
               clientId,
               scope: 'name email',
               redirectURI,
               usePopup: true,
            });

            setIsReady(true);
         } catch (error) {
            console.error('Error initializing Apple Sign-In:', error);
         }
      };

      if (existingScript && window.AppleID) {
         initApple();
      } else if (!existingScript) {
         const script = document.createElement('script');
         script.src = APPLE_JS_SRC;
         script.async = true;
         script.defer = true;
         script.onload = initApple;
         script.onerror = () => {
            console.error('Failed to load Apple Sign-In script');
         };
         document.body.appendChild(script);
      }
   }, []);

   const signInWithApple = useCallback(async () => {
      if (!window.AppleID || !window.AppleID.auth) {
         const errorMsg =
            'Sign in with Apple is not available. Please refresh the page or try again later.';
         if (onError) onError(errorMsg);
         else toast.error(errorMsg);
         return;
      }

      try {
         const response = await window.AppleID.auth.signIn();
         const idToken = response?.authorization?.id_token;

         if (!idToken) {
            const errorMsg = 'No credential received from Apple';
            if (onError) onError(errorMsg);
            else toast.error(errorMsg);
            return;
         }

         await onSuccess(idToken);
      } catch (error: any) {
         // Apple uses specific error codes for user cancellation
         if (
            error?.error === 'user_cancelled_login' ||
            error?.error === 'user_cancelled_authorize'
         ) {
            return;
         }

         const errorMsg = 'Could not sign in with Apple. Please try again.';
         if (onError) onError(errorMsg);
         else toast.error(errorMsg);
      }
   }, [onSuccess, onError]);

   return {
      signInWithApple,
      isAppleReady: isReady,
   };
}

